import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  BarChart3,
  Calendar,
  FileSpreadsheet,
  DollarSign,
  ShoppingBag,
  CreditCard,
  Package2,
  AlertTriangle,
  XCircle,
  Users,
  UserPlus,
  Loader2,
  TrendingUp,
  Clock
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatDate, formatPrice } from '../../utils/format';
import { fetchWithAuth } from '../../hooks/useAuth';
import { SalesReport, InventoryReport, CustomerReport } from '../../types/reports';

export function ReportsPage() {
  const [dateRange, setDateRange] = React.useState({
    startDate: '',
    endDate: '',
  });

  // Fetch sales report
  const { data: salesReport, isLoading: isSalesLoading } = useQuery<SalesReport>({
    queryKey: ['sales-report', dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange.endDate) params.append('endDate', dateRange.endDate);

      const response = await fetchWithAuth(`/api/admin/reports/sales?${params}`);
      if (!response.ok) throw new Error('Eroare la obținerea raportului de vânzări');
      return response.json();
    },
  });

  // Fetch inventory report
  const { data: inventoryReport, isLoading: isInventoryLoading } = useQuery<InventoryReport>({
    queryKey: ['inventory-report'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/reports/inventory');
      if (!response.ok) throw new Error('Eroare la obținerea raportului de inventar');
      return response.json();
    },
  });

  // Fetch customer report
  const { data: customerReport, isLoading: isCustomerLoading } = useQuery<CustomerReport>({
    queryKey: ['customer-report'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/reports/customers');
      if (!response.ok) throw new Error('Eroare la obținerea raportului de clienți');
      return response.json();
    },
  });

  const isLoading = isSalesLoading || isInventoryLoading || isCustomerLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rapoarte</h1>
            <p className="text-sm text-gray-500">
              Analiză și statistici
            </p>
          </div>
        </div>
        <Button 
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          onClick={async () => {
            try {
              const response = await fetchWithAuth('/api/admin/reports/export', {
                method: 'GET',
                headers: {
                  'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
              });
              if (!response.ok) {
                throw new Error('Eroare la exportul rapoartelor');
              }
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `raport-${new Date().toISOString().split('T')[0]}.xlsx`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch (error) {
              console.error('Export error:', error);
            }
          }}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Export Excel
        </Button>
      </div>

      {/* Date Range Filter */}
      <Card className="bg-white shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Data Început
            </label>
            <Input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange((d) => ({ ...d, startDate: e.target.value }))
              }
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Data Sfârșit
            </label>
            <Input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange((d) => ({ ...d, endDate: e.target.value }))
              }
              className="mt-1"
            />
          </div>
        </div>
      </Card>

      {/* Sales Report */}
      {salesReport && (
        <>
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Raport Vânzări
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <ShoppingBag className="w-4 h-4" />
                      Total Comenzi
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      {salesReport.totalOrders || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <DollarSign className="w-4 h-4" />
                      Venit Total
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatPrice(salesReport.totalRevenue || 0)}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <CreditCard className="w-4 h-4" />
                      Valoare Medie Comandă
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatPrice(salesReport.averageOrderValue || 0)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {salesReport.topProducts && salesReport.topProducts.length > 0 && (
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Top Produse
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Produs</TableHead>
                      <TableHead className="font-semibold">Cantitate</TableHead>
                      <TableHead className="font-semibold">Venit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesReport.topProducts.map((product) => (
                      <TableRow key={product.name} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell>{product.quantity || 0}</TableCell>
                        <TableCell>{formatPrice(product.revenue || 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {salesReport.recentOrders && salesReport.recentOrders.length > 0 && (
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Comenzi Recente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">ID</TableHead>
                      <TableHead className="font-semibold">Data</TableHead>
                      <TableHead className="font-semibold">Client</TableHead>
                      <TableHead className="font-semibold">Total</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesReport.recentOrders.map((order) => (
                      <TableRow key={order.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">#{order.id}</TableCell>
                        <TableCell>{formatDate(order.date)}</TableCell>
                        <TableCell>{order.customer}</TableCell>
                        <TableCell>{formatPrice(order.total || 0)}</TableCell>
                        <TableCell>
                          <Badge className={
                            order.status === 'COMPLETED'
                              ? 'bg-green-100 text-green-800 ring-1 ring-green-200'
                              : order.status === 'PENDING'
                              ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200'
                              : 'bg-gray-100 text-gray-800 ring-1 ring-gray-200'
                          }>
                            {order.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Inventory Report */}
      {inventoryReport && (
        <>
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package2 className="w-5 h-5" />
                Raport Inventar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <Package2 className="w-4 h-4" />
                      Total Produse
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      {inventoryReport.totalProducts || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <AlertTriangle className="w-4 h-4" />
                      Stoc Redus
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600">
                      {inventoryReport.lowStock || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <XCircle className="w-4 h-4" />
                      Stoc Epuizat
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      {inventoryReport.outOfStock || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <DollarSign className="w-4 h-4" />
                      Valoare Totală
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatPrice(inventoryReport.totalValue || 0)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {inventoryReport.products && inventoryReport.products.length > 0 && (
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package2 className="w-5 h-5" />
                  Detalii Produse
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Produs</TableHead>
                      <TableHead className="font-semibold">Stoc</TableHead>
                      <TableHead className="font-semibold">Valoare</TableHead>
                      <TableHead className="font-semibold">Ultima Actualizare</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventoryReport.products.map((product) => (
                      <TableRow key={product.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell>
                          <Badge className={
                            product.stock > 10
                              ? 'bg-green-100 text-green-800 ring-1 ring-green-200'
                              : product.stock > 0
                              ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200'
                              : 'bg-red-100 text-red-800 ring-1 ring-red-200'
                          }>
                            {product.stock || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatPrice(product.value || 0)}</TableCell>
                        <TableCell>{formatDate(product.lastUpdated)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Customer Report */}
      {customerReport && (
        <>
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Raport Clienți
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <Users className="w-4 h-4" />
                      Total Clienți
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      {customerReport.totalCustomers || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <UserPlus className="w-4 h-4" />
                      Clienți Noi
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {customerReport.newCustomers || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <DollarSign className="w-4 h-4" />
                      Venit Total
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatPrice(customerReport.totalRevenue || 0)}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <CreditCard className="w-4 h-4" />
                      Valoare Medie Comandă
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatPrice(customerReport.averageOrderValue || 0)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {customerReport.topCustomers && customerReport.topCustomers.length > 0 && (
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Top Clienți
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Client</TableHead>
                      <TableHead className="font-semibold">Comenzi</TableHead>
                      <TableHead className="font-semibold">Total Cheltuit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customerReport.topCustomers.map((customer) => (
                      <TableRow key={customer.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell>{customer.orders || 0}</TableCell>
                        <TableCell>{formatPrice(customer.spent || 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
